import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle,
  XCircle,
  WarningCircle,
  Clock,
  UploadSimple,
  Paperclip,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  buildChecklistRows,
  listDocumentTypes,
  reviewDocument,
  statusTone,
  summariseChecklist,
} from "@/lib/documentChecklist";
import {
  deleteEnrollmentDocument,
  enrollmentDocumentSignedUrl,
  listEnrollmentDocuments,
  uploadEnrollmentDocument,
} from "@/lib/storage";
import { isSupabaseConfigured } from "@/lib/supabase";
import { humanFileSize } from "@/lib/storage";

const ICON_FOR_STATUS = {
  approved: CheckCircle,
  uploaded: Clock,
  rejected: WarningCircle,
  missing: XCircle,
};

/**
 * Shared document checklist. Admin sees approve/reject buttons; parent
 * sees upload per row + retry on rejection.
 */
export default function DocumentChecklist({
  enrollment,
  organizationId,
  role,              // "parent" | "admin"
  canUpload = true,
  uploadedBy,
  onSummaryChange,   // ({missingRequired, rejectedCount, ...}) => void
  testId = "document-checklist",
}) {
  const [types, setTypes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured || !enrollment?.id) return;
    (async () => {
      setLoading(true);
      try {
        const [ts, ds] = await Promise.all([
          listDocumentTypes(organizationId, { scope: "enrollment" }),
          listEnrollmentDocuments(enrollment.id),
        ]);
        setTypes(ts);
        setDocuments(ds);
      } catch (err) {
        toast.error(err.message || "Could not load document checklist");
      } finally {
        setLoading(false);
      }
    })();
  }, [enrollment?.id, organizationId]);

  const rows = useMemo(() => buildChecklistRows(types, documents), [types, documents]);
  const summary = useMemo(() => summariseChecklist(rows), [rows]);

  useEffect(() => {
    onSummaryChange?.(summary);
  }, [summary, onSummaryChange]);

  const onUpload = async (type, file) => {
    try {
      const row = await uploadEnrollmentDocument({
        enrollmentId: enrollment.id,
        file,
        documentTypeId: type?.id || null,
        documentType: type?.code || null,
        uploadedBy,
      });
      setDocuments((prev) => [...prev, row]);
      toast.success(type ? `${type.label} uploaded` : "Document uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    }
  };

  const onReplace = async (type, file) => {
    try {
      const row = await uploadEnrollmentDocument({
        enrollmentId: enrollment.id,
        file,
        documentTypeId: type?.id || null,
        documentType: type?.code || null,
        uploadedBy,
      });
      setDocuments((prev) => [...prev, row]);
      toast.success(
        type
          ? `${type.label} re-uploaded — awaiting review`
          : "Re-uploaded — awaiting review",
      );
    } catch (err) {
      toast.error(err.message || "Re-upload failed");
    }
  };

  const onDelete = async (doc) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await deleteEnrollmentDocument({
        id: doc.id,
        storagePath: doc.storage_path,
      });
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
      toast.success("Removed");
    } catch (err) {
      toast.error(err.message || "Could not remove");
    }
  };

  const onReview = async (doc, status) => {
    let note = null;
    if (status === "rejected") {
      note = window.prompt("Reason for rejection (visible to parent):") || null;
      if (note === null) return;
    }
    try {
      const updated = await reviewDocument({
        documentId: doc.id,
        status,
        note,
      });
      setDocuments((prev) =>
        prev.map((d) => (d.id === updated.id ? updated : d)),
      );
      toast.success(status === "approved" ? "Approved" : "Rejected");
    } catch (err) {
      toast.error(err.message || "Review failed");
    }
  };

  if (loading) {
    return (
      <section
        data-testid={testId}
        className="flex items-center justify-center rounded-2xl border border-neutral-200 bg-white p-10"
      >
        <div className="h-6 w-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </section>
    );
  }

  return (
    <section
      data-testid={testId}
      className="rounded-2xl border border-neutral-200 bg-white p-5"
    >
      <div className="mb-3 flex items-center gap-2">
        <Paperclip size={18} weight="duotone" className="text-primary-hover" />
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Required documents
        </p>
        <span
          data-testid={`${testId}-summary`}
          className="ml-auto rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-700"
        >
          {summary.approvedCount + summary.uploadedCount}/{summary.totalRequired} complete
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="rounded-xl bg-neutral-50 px-3 py-4 text-center text-xs text-neutral-500">
          No required documents configured for this organization.
        </p>
      ) : (
        <ul className="space-y-2" data-testid={`${testId}-list`}>
          {rows.map((row, i) => (
            <ChecklistRow
              key={row.type?.id || `untyped-${i}`}
              row={row}
              role={role}
              canUpload={canUpload}
              onUpload={(file) => onUpload(row.type, file)}
              onReplace={(file) => onReplace(row.type, file)}
              onDelete={onDelete}
              onReview={onReview}
              testId={testId}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function ChecklistRow({
  row, role, canUpload, onUpload, onReplace, onDelete, onReview, testId,
}) {
  const { type, documents, effectiveStatus } = row;
  const tone = statusTone(effectiveStatus);
  const Icon = ICON_FOR_STATUS[effectiveStatus] || Clock;
  const [signedUrl, setSignedUrl] = useState(null);

  const latest = documents[documents.length - 1];
  const latestRejected = effectiveStatus === "rejected";

  useEffect(() => {
    if (!latest) return;
    (async () => {
      try {
        const url = await enrollmentDocumentSignedUrl(latest.storage_path);
        setSignedUrl(url);
      } catch {
        /* ignore */
      }
    })();
  }, [latest?.storage_path]); // eslint-disable-line react-hooks/exhaustive-deps

  const rowId = type?.id || latest?.id || "row";

  return (
    <li
      data-testid={`${testId}-row-${type?.code || rowId}`}
      data-status={effectiveStatus}
      className="flex flex-col gap-3 rounded-2xl border border-neutral-200 bg-white p-3 sm:flex-row sm:items-start sm:justify-between"
    >
      <div className="flex min-w-0 items-start gap-3">
        <span
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${tone.bg} ${tone.color}`}
        >
          <Icon size={16} weight="duotone" />
        </span>
        <div className="min-w-0">
          <p className="font-heading text-sm font-semibold tracking-tight text-neutral-900">
            {type?.label || "Additional document"}
            {type?.is_required ? (
              <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                Required
              </span>
            ) : type ? (
              <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-neutral-400">
                Optional
              </span>
            ) : null}
          </p>
          {type?.description && (
            <p className="mt-0.5 text-xs text-neutral-500">{type.description}</p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span
              data-testid={`${testId}-row-${type?.code || rowId}-status`}
              className={`inline-flex items-center gap-1 rounded-full border ${tone.ring} ${tone.bg} ${tone.color} px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider`}
            >
              {tone.label}
            </span>
            {latest?.created_at && (
              <span className="text-[10px] text-neutral-500">
                Uploaded {new Date(latest.created_at).toLocaleDateString()}
              </span>
            )}
            {latest?.size_bytes ? (
              <span className="text-[10px] text-neutral-500">
                · {humanFileSize(latest.size_bytes)}
              </span>
            ) : null}
            {signedUrl && (
              <a
                href={signedUrl}
                target="_blank"
                rel="noreferrer"
                data-testid={`${testId}-row-${type?.code || rowId}-view`}
                className="text-[10px] font-semibold uppercase tracking-wider text-primary-hover hover:underline"
              >
                View
              </a>
            )}
          </div>
          {latest?.review_note && effectiveStatus === "rejected" && (
            <p
              data-testid={`${testId}-row-${type?.code || rowId}-note`}
              className="mt-2 rounded-lg bg-[#C6851E]/10 px-2 py-1.5 text-xs text-[#C6851E]"
            >
              <span className="font-bold">Admin note:</span> {latest.review_note}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center gap-2">
        {/* Parent actions */}
        {role === "parent" && canUpload && (effectiveStatus === "missing") && (
          <UploadButton
            testId={`${testId}-row-${type?.code || rowId}-upload`}
            label="Upload"
            onPick={onUpload}
          />
        )}
        {role === "parent" && canUpload && latestRejected && (
          <UploadButton
            testId={`${testId}-row-${type?.code || rowId}-reupload`}
            label="Re-upload"
            accent
            onPick={(f) => onReplace(f)}
          />
        )}
        {/* Admin actions */}
        {role === "admin" && effectiveStatus === "uploaded" && latest && (
          <>
            <button
              type="button"
              data-testid={`${testId}-row-${type?.code || rowId}-approve`}
              onClick={() => onReview(latest, "approved")}
              className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-hover"
            >
              Approve
            </button>
            <button
              type="button"
              data-testid={`${testId}-row-${type?.code || rowId}-reject`}
              onClick={() => onReview(latest, "rejected")}
              className="rounded-full border border-[#C6851E]/50 bg-white px-3 py-1.5 text-xs font-semibold text-[#C6851E] hover:bg-[#C6851E]/10"
            >
              Reject
            </button>
          </>
        )}
        {role === "admin" && effectiveStatus === "approved" && latest && (
          <button
            type="button"
            data-testid={`${testId}-row-${type?.code || rowId}-unapprove`}
            onClick={() => onReview(latest, "rejected")}
            className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50"
          >
            Revoke
          </button>
        )}
        {role === "admin" && latest && (
          <button
            type="button"
            aria-label="Delete document"
            data-testid={`${testId}-row-${type?.code || rowId}-delete`}
            onClick={() => onDelete(latest)}
            className="rounded-full p-1.5 text-neutral-400 hover:bg-[#C6851E]/10 hover:text-[#C6851E]"
          >
            <XCircle size={16} weight="bold" />
          </button>
        )}
      </div>
    </li>
  );
}

function UploadButton({ label, onPick, accent, testId }) {
  const inputId = `${testId}-input`;
  return (
    <>
      <label
        htmlFor={inputId}
        data-testid={testId}
        className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm transition-all ${
          accent
            ? "bg-[#C6851E] text-white hover:bg-[#C6851E]"
            : "border border-primary/40 bg-primary/5 text-primary-hover hover:bg-primary/10"
        }`}
      >
        <UploadSimple size={12} weight="bold" />
        {label}
      </label>
      <input
        id={inputId}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.heic,.doc,.docx"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = "";
        }}
      />
    </>
  );
}
